import MDEditor from "@uiw/react-md-editor";
import { useCallback } from "react";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  preview?: "edit" | "live" | "preview";
}

const MarkdownEditor = ({
  value,
  onChange,
  placeholder = "Write your content here...",
  height = 400,
  preview = "live",
}: MarkdownEditorProps) => {
  const handleChange = useCallback(
    (val: string | undefined) => {
      onChange(val ?? "");
    },
    [onChange]
  );

  return (
    <div data-color-mode="light" className="markdown-editor-wrapper">
      <MDEditor
        value={value}
        onChange={handleChange}
        preview={preview}
        height={height}
        textareaProps={{
          placeholder,
        }}
        visibleDragbar={false}
      />
      <style>{`
        .markdown-editor-wrapper .w-md-editor {
          border-radius: 0.75rem;
          border: 1px solid rgb(226 232 240);
          box-shadow: none;
        }
        .markdown-editor-wrapper .w-md-editor:focus-within {
          border-color: var(--color-primary, #10b981);
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
        }
        .markdown-editor-wrapper .w-md-editor-toolbar {
          border-radius: 0.75rem 0.75rem 0 0;
          border-bottom: 1px solid rgb(226 232 240);
          background: rgb(248 250 252);
        }
        .markdown-editor-wrapper .w-md-editor-content {
          border-radius: 0 0 0.75rem 0.75rem;
        }
        .markdown-editor-wrapper .w-md-editor-text-pre > code,
        .markdown-editor-wrapper .w-md-editor-text-input {
          font-size: 14px !important;
          line-height: 1.6 !important;
        }
        .markdown-editor-wrapper .w-md-editor-preview {
          padding: 16px;
        }
        .markdown-editor-wrapper .wmde-markdown {
          font-size: 14px;
        }

        /* Table styling */
        .markdown-editor-wrapper .wmde-markdown table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        .markdown-editor-wrapper .wmde-markdown table th,
        .markdown-editor-wrapper .wmde-markdown table td {
          border: 1px solid rgb(226 232 240);
          padding: 8px 12px;
          text-align: left;
        }
        .markdown-editor-wrapper .wmde-markdown table th {
          background-color: rgb(248 250 252);
          font-weight: 600;
        }
        .markdown-editor-wrapper .wmde-markdown table tr:nth-child(even) {
          background-color: rgb(249 250 251);
        }

        /* Image styling */
        .markdown-editor-wrapper .wmde-markdown img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
        }

        /* Headings */
        .markdown-editor-wrapper .wmde-markdown h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          border-bottom: 1px solid rgb(226 232 240);
          padding-bottom: 0.5rem;
        }
        .markdown-editor-wrapper .wmde-markdown h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
        }
        .markdown-editor-wrapper .wmde-markdown h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem;
        }

        /* Lists */
        .markdown-editor-wrapper .wmde-markdown ul,
        .markdown-editor-wrapper .wmde-markdown ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .markdown-editor-wrapper .wmde-markdown li {
          margin: 0.25rem 0;
        }

        /* Code blocks */
        .markdown-editor-wrapper .wmde-markdown pre {
          background-color: rgb(248 250 252);
          border-radius: 6px;
          padding: 12px;
          overflow-x: auto;
        }
        .markdown-editor-wrapper .wmde-markdown code {
          background-color: rgb(241 245 249);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
        }
        .markdown-editor-wrapper .wmde-markdown pre code {
          background: none;
          padding: 0;
        }

        /* Blockquote */
        .markdown-editor-wrapper .wmde-markdown blockquote {
          border-left: 4px solid rgb(16 185 129);
          padding-left: 1rem;
          margin: 1rem 0;
          color: rgb(100 116 139);
        }

        /* Links */
        .markdown-editor-wrapper .wmde-markdown a {
          color: rgb(16 185 129);
          text-decoration: none;
        }
        .markdown-editor-wrapper .wmde-markdown a:hover {
          text-decoration: underline;
        }

        /* Dark mode support */
        .dark .markdown-editor-wrapper {
          --color-canvas-default: rgb(2 6 23);
          --color-canvas-subtle: rgb(15 23 42);
        }
        .dark .markdown-editor-wrapper .w-md-editor {
          border-color: rgb(51 65 85);
          background-color: rgb(2 6 23);
        }
        .dark .markdown-editor-wrapper .w-md-editor-toolbar {
          background: rgb(15 23 42);
          border-color: rgb(51 65 85);
        }
        .dark .markdown-editor-wrapper .w-md-editor-toolbar li > button {
          color: rgb(148 163 184);
        }
        .dark .markdown-editor-wrapper .w-md-editor-toolbar li > button:hover {
          color: white;
          background-color: rgb(30 41 59);
        }
        .dark .markdown-editor-wrapper .w-md-editor-text-pre > code,
        .dark .markdown-editor-wrapper .w-md-editor-text-input,
        .dark .markdown-editor-wrapper .w-md-editor-text {
          color: rgb(226 232 240) !important;
        }
        .dark .markdown-editor-wrapper .w-md-editor-text-input::placeholder {
          color: rgb(100 116 139);
        }
        .dark .markdown-editor-wrapper .wmde-markdown {
          background-color: rgb(2 6 23);
          color: rgb(226 232 240);
        }
        .dark .markdown-editor-wrapper .w-md-editor-preview {
          background-color: rgb(2 6 23);
        }
        .dark .markdown-editor-wrapper .wmde-markdown table th,
        .dark .markdown-editor-wrapper .wmde-markdown table td {
          border-color: rgb(51 65 85);
        }
        .dark .markdown-editor-wrapper .wmde-markdown table th {
          background-color: rgb(15 23 42);
        }
        .dark .markdown-editor-wrapper .wmde-markdown table tr:nth-child(even) {
          background-color: rgb(15 23 42);
        }
        .dark .markdown-editor-wrapper .wmde-markdown pre,
        .dark .markdown-editor-wrapper .wmde-markdown code {
          background-color: rgb(15 23 42);
        }
        .dark .markdown-editor-wrapper .wmde-markdown h1 {
          border-bottom-color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
};

export default MarkdownEditor;
